import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Medal } from 'lucide-react'
import { supabase } from '@/lib/supabase/client'
import { useAuthStore } from '@/stores/auth.store'
import type { Database } from '@/lib/supabase/types'

type LeaderboardRow = Database['public']['Tables']['leaderboard']['Row']

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <Medal size={16} className="text-yellow-400" />
  if (rank === 2) return <Medal size={16} className="text-gray-300" />
  if (rank === 3) return <Medal size={16} className="text-amber-600" />
  return <span className="text-xs font-mono text-gray-500 w-4 text-center">{rank}</span>
}

function PlayerAvatar({ url, name }: { url?: string | null; name: string }) {
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        className="w-8 h-8 rounded-full object-cover border border-arcade-border"
      />
    )
  }
  const initial = (name[0] ?? '?').toUpperCase()
  return (
    <div className="w-8 h-8 rounded-full bg-arcade-accent flex items-center justify-center border border-arcade-border">
      <span className="text-white text-xs font-bold font-mono">{initial}</span>
    </div>
  )
}

export default function Leaderboard() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [rows, setRows] = useState<LeaderboardRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      setLoading(true)
      const { data, error: err } = await supabase
        .from('leaderboard')
        .select('*')
        .order('total_xp', { ascending: false })
        .limit(50)

      if (err) {
        setError(err.message)
      } else {
        setRows(data ?? [])
      }
      setLoading(false)
    }
    load()
  }, [])

  return (
    <main className="min-h-screen bg-arcade-bg text-white">
      <header className="border-b border-arcade-border px-6 py-4 flex items-center gap-4">
        <button
          onClick={() => navigate('/games')}
          className="p-2 rounded-lg text-gray-400 hover:text-white transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <div>
          <h1 className="text-lg font-bold">Leaderboard</h1>
          <p className="text-xs text-gray-500 font-mono">Top players by XP</p>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-4 py-8">
        {loading && (
          <div className="flex justify-center py-16">
            <div className="w-6 h-6 border-2 border-arcade-neon border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {error && (
          <div className="text-center py-16">
            <p className="text-red-400 text-sm font-mono">{error}</p>
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div className="text-center py-16 space-y-3">
            <p className="text-gray-400">No players on the leaderboard yet.</p>
            <p className="text-gray-600 text-sm">Complete challenges to earn XP and claim a spot.</p>
            <button
              onClick={() => navigate('/play/algoquest')}
              className="mt-2 text-sm text-arcade-accent hover:underline"
            >
              Start playing →
            </button>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="flex flex-col gap-2">
            {rows.map((row, i) => {
              const rank = row.rank ?? i + 1
              const name = row.username ?? 'Anonymous'
              const isMe = user?.id === row.user_id

              return (
                <div
                  key={row.user_id}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors ${
                    isMe
                      ? 'bg-arcade-neon/5 border-arcade-neon/40'
                      : 'bg-arcade-surface border-arcade-border'
                  }`}
                >
                  <div className="w-5 flex items-center justify-center flex-shrink-0">
                    <RankBadge rank={rank} />
                  </div>
                  <PlayerAvatar url={row.avatar_url} name={name} />
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold text-sm truncate ${isMe ? 'text-arcade-neon' : 'text-white'}`}>
                      {name}
                      {isMe && <span className="ml-2 text-xs font-mono text-arcade-neon/60">(you)</span>}
                    </p>
                    <p className="text-xs text-gray-500 font-mono">{row.algos_solved} solved</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-mono font-bold text-arcade-neon">{row.total_xp.toLocaleString()}</p>
                    <p className="text-xs text-gray-600 font-mono">XP</p>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </main>
  )
}
